import map from "lodash/fp/map";
import first from "lodash/fp/first";
import last from "lodash/fp/last";
import join from "lodash/fp/join";
import findIndex from "lodash/fp/findIndex";
import multiply from "lodash/fp/multiply";
import random from "lodash/fp/random";
import reverse from "lodash/fp/reverse";
import sumBy from "lodash/fp/sumBy";
import sortByDistance from "sort-by-distance";
import mapsLoader from "google-maps";

class Controller {
  constructor(Mapie, $injector, $mdDialog, GoogleMaps) {
    "ngInject";

    this.Mapie = Mapie;
    this.$mdDialog = $mdDialog;
    this.Events = $injector.get("Events");
    /*
      Actually we are loading google maps scripts twice due to a lack of organization.
      There is large logic that uses ng-map to render and another is using google maps api.
      This update has been done because we want not to expose the google maps api key where it's not needed.
      We encapsulated all the logic into each component so google maps key is not exposed unless
      user is logged in and is opening a component that uses google maps.
    */

    this.googleMapsUrl = GoogleMaps.getSource();

    this.getDistance = (googleMap) => {
      const distance = this.Mapie.getDistanceFromMap(googleMap);
      const distanceText = `Route length is ${distance}`;
      if (distance) this.Mapie.addDistanceLayer(distanceText);
    };

    if (this.mode && this.mode === "edit") {
      // And this one for google maps api used in the controller
      mapsLoader.KEY = process.env.GOOGLE_MAPS_API_KEY;
      mapsLoader.load((google) => {
        const directionsService = new google.maps.DirectionsService();

        this.fetchOptimizedDirection = () => {
          directionsService.route({
            origin: this.editableOrigin,
            destination: this.editableDestination,
            waypoints: this.editablePoints,
            optimizeWaypoints: true,
            travelMode: "DRIVING",
          });
        };

        this.applyEditEvents = (googleMap) => {
          google.maps.event.addListener(googleMap, "idle", () => {
            const distance = this.Mapie.getDistanceFromMap(googleMap);
            const distanceText =
              this.mode && this.mode === "edit"
                ? distance
                : `Route length is ${distance}`;
            const mode =
              this.mode === "edit" && !this.showOriginal ? "edit" : "";

            if (distance) this.Mapie.addDistanceLayer(distanceText, mode);
          });
        };
      });
    }
  }

  $onInit() {
    const _that = this;
    this.editableStations = [];

    const markerImportance = (ratio) => {
      let importance = "low";
      if (ratio > 60) {
        importance = "high";
      } else if (ratio > 30) {
        importance = "medium";
      }
      return importance;
    };

    const sumTotalRouteWeight = (stations) => {
      const sumInOut = (station) =>
        station.ingoingWeight + station.outgoingWeight;

      return sumBy(sumInOut, stations);
    };

    const formatPoints = (station) => {
      const location = [
        last(station.loc.coordinates),
        first(station.loc.coordinates),
      ];
      const importanceRatio =
        ((station.ingoingWeight + station.outgoingWeight) /
          this.totalRouteWeight) *
          100 || 0;
      const importanceLevel = markerImportance(importanceRatio);
      const markerIcon = {
        url: `../../images/station/pin-${importanceLevel}.svg`,
        scaledSize: [40, 56],
      };

      return { ...station, location, markerIcon, stopover: true };
    };

    const formatStations = (station) => {
      const location = {
        lat: last(station.loc.coordinates),
        lng: first(station.loc.coordinates),
      };

      return { location, stopover: true };
    };

    const formatHotLocations = (point) => ({
      ...point,
      isSuggested: true,
      location: [point.lat, point.lng],
    });

    const getRandomId = () => multiply(random(0, 5000000), random(0, 5000000));

    const getStringPosition = (origin) =>
      `${origin.location.lat},${origin.location.lng}`;

    const mapPaths = (polygon) => ({ paths: this.Mapie.formatPaths(polygon) });

    if (this.polygon1 && this.polygon2) {
      this.polygon1Shapes = map(mapPaths, this.polygon1.loc.coordinates);
      this.polygon2Shapes = map(mapPaths, this.polygon2.loc.coordinates);
    }

    if (this.hotLocations) {
      this.hotLocationsPoints = map(formatHotLocations)(this.hotLocations);
    }

    const getConfirmDialog = (title, details) =>
      this.$mdDialog
        .confirm()
        .title(title)
        .textContent(details)
        .ok("Ok")
        .cancel("Cancel");

    const sortStations = (stations) => {
      const mappedStations = map((item) => ({
        ...item,
        longitude: item.loc.coordinates[0],
        latitude: item.loc.coordinates[1],
      }))(stations);

      const originStation = mappedStations.shift();
      const opts = { yName: "latitude", xName: "longitude" };
      const sortedStations = sortByDistance(
        originStation,
        mappedStations,
        opts
      );

      return [originStation, ...sortedStations];
    };

    const setLocationSort = (idx, coordinates) => {
      this.editableStations[idx].loc.coordinates = coordinates;
      const sortedStations = sortStations(this.editableStations);
      this.isModified = true;
      this.generateEditableStations(sortedStations);
      return sortedStations;
    };

    this.onMapInit = (googleMap) => {
      this.map = googleMap;
      this.getDistance(map);
      if (this.mode === "edit") {
        this.applyEditEvents(googleMap);
        this.showOriginal = false;
        this.createAllowed = false;
        this.optimizationAllowed = true;
        this.showHeatmap = false;
        this.showPolygons = false;
        this.editableStations = this.stations;
        this.generateEditableStations(this.editableStations);
        this.fetchOptimizedDirection();
      }
    };

    this.addStation = (e) => {
      if (!this.createAllowed) return;
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      const coordinates = [lng, lat];
      const addStation = getConfirmDialog(
        "Add Station",
        `Are you sure you want to add station in this lat, long ${join(
          ", ",
          reverse(coordinates)
        )}`
      );

      this.$mdDialog.show(addStation).then(
        () => {
          const _id = getRandomId();
          const location = `${lat}, ${lng}`;
          const name = `station - ${_id}`;

          const newStation = {
            _id,
            name,
            location,
            loc: { coordinates },
            lat,
            lng,
          };

          this.editableStations.push(newStation);
          const sortedStations = sortStations(this.editableStations);

          this.isModified = true;
          this.generateEditableStations(sortedStations);
          this.Events.emit(this.updateStationsEvent, sortedStations);
          this.Events.emit(this.updateDemandEvent);
        },
        () => {}
      );
    };

    this.markerPoints = map(formatPoints)(this.stations);
    this.softStationsMarkerPoints = map(formatPoints)(this.softStations);
    this.points = map(formatStations)(this.stations);
    this.origin = getStringPosition(this.points.shift());
    this.destination = getStringPosition(this.points.pop());

    this.showDetails = function (event, point, markerId) {
      _that.currentPoint = point;
      _that.map.showInfoWindow(event, "stationInfoWindow", markerId);
    };

    this.generateEditableStations = (stations) => {
      this.totalRouteWeight = sumTotalRouteWeight(stations);
      this.editableMarkerPoints = map(formatPoints)(stations);
      this.editablePoints = map(formatStations)(stations);
      this.editableOrigin = getStringPosition(this.editablePoints.shift());
      this.editableDestination = getStringPosition(this.editablePoints.pop());
    };

    this.toggleAllowOptimization = (status) => {
      this.Events.emit(this.toggleOptimizationEvent, status);
      this.optimizationAllowed = status;
    };

    this.toggleCreateMode = (status) => {
      this.Events.emit(this.toggleCreateModeEvent, status);
      this.createAllowed = status;
    };

    this.changeStationPosition = (e, point) => {
      const findId = (item) => item._id === point._id;
      const stationIdx = findIndex(findId, this.editableStations);
      const coordinates = [e.latLng.lng(), e.latLng.lat()];
      const oldCoordinates = this.editableStations[stationIdx].loc.coordinates;
      const sortedStations = setLocationSort(stationIdx, coordinates);

      const changePosition = getConfirmDialog(
        "Change Station Position",
        `Are you sure you want to move station to this lat, long ${join(
          ", ",
          reverse(coordinates)
        )}`
      );

      this.$mdDialog.show(changePosition).then(
        () => {
          this.Events.emit(this.updateStationsEvent, sortedStations);
          this.Events.emit(this.updateDemandEvent);
        },
        () => {
          setLocationSort(stationIdx, oldCoordinates);
        }
      );
    };

    this.removeStation = (station) => {
      const findById = (item) => item._id === station._id;
      const stationIdx = findIndex(findById, this.stations);
      const removeStation = getConfirmDialog(
        "Remove Station",
        `Are you sure you want to remove station " ${this.stations[stationIdx].name} "`
      );

      this.$mdDialog.show(removeStation).then(
        () => {
          this.isModified = true;
          _that.currentPoint = null;
          _that.map.hideInfoWindow("stationInfoWindow");
          if (stationIdx >= 0) this.stations.splice(stationIdx, 1);
          this.Events.emit(this.updateStationsEvent, this.stations);
          this.Events.emit(this.updateDemandEvent);
        },
        () => {}
      );
    };

    this.updateStationInfo = (station) => {
      this.Events.emit(this.editStationEvent, station);
    };

    this.updateStations = (stations) => {
      this.isModified = true;
      this.editableStations = stations;
      this.generateEditableStations(this.editableStations);
    };

    this.toggleRoutes = () => {
      this.showOriginal = !this.showOriginal;
    };

    this.Events.on(this.updateStationsEvent, this.updateStations);
  }
}

export default Controller;



// WEBPACK FOOTER //
// ./src/components/directionMap/directionMap.controller.js